/**
 * ReportEditsContext — Admin overlay + translation overlay for report text.
 *
 * Workflow: click pen on a field → modal with TipTap editor opens → admin
 * edits → Tallenna → context.save() PUTs to backend (append-only history,
 * migration 092). Latest row per (instance_id, field_key) wins on read.
 *
 * Translation (migration 136): when viewing language differs from the
 * instance language, get() prefers report_translations for that language.
 * Admin editing is bound to the instance language — the pen is hidden in
 * translated views so edits never fork the source of truth.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS, withBackendPath } from '../../config/api';
import { hasRole } from '../../utils/roles';
import { canOnInstance } from '../../utils/access';
import { normalizeInstanceLanguage } from '../../../../../packages/shared/instanceLanguage.js';
import { normalizeReportLanguage, hasStaticUi } from '@kuutio/shared/reportLanguages.js';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Ctx = createContext(null);

const NOOP_VALUE = Object.freeze({
  canEdit: false,
  me: null,
  editMode: false,
  setEditMode: () => {},
  editSessionId: null,
  get: (_fieldKey, fallback = '') => fallback,
  // Translation-only: returns fallback verbatim outside a translated view
  // so derived strings stay byte-identical on the default report.
  getTranslated: (_fieldKey, fallback = '') => fallback,
  isEdited: () => false,
  isTranslated: () => false,
  getOriginal: (_fieldKey, fallback = '') => fallback,
  save: () => Promise.resolve(),
  savingKeys: new Set(),
  edits: {},
  translations: {},
  instanceId: null,
  instanceLanguage: 'fi',
  viewingLanguage: 'fi',
  isTranslatedView: false,
  availableLanguages: ['fi'],
  setViewLanguage: () => {},
});

export function useReportEdits() {
  return useContext(Ctx) || NOOP_VALUE;
}

/**
 * Provider lives at the App root so the right-drawer panel (rendered via
 * a portal under DrawerProvider) still sees it. instanceId is resolved in
 * this priority order:
 *   1. `instanceIdProp` (caller-provided)
 *   2. ?kid= / ?instance= / ?instanceId= query params (legacy report URLs)
 *   3. /raportti/<slug> or /report/<slug> path slug, resolved via the
 *      by-slug API. Without this branch the pretty share URLs would
 *      have a null instanceId here even though PublicWorkshopReport
 *      itself resolves the slug for its data fetches — the symptom was
 *      "different report on the pretty URL" because no overlay edits
 *      (titles, image URLs, version history) ever loaded.
 * On routes that match none, instanceId stays null and no fetches happen.
 */
export function ReportEditsProvider({ instanceId: instanceIdProp, children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { language: uiLanguage, setLanguage: setUiLanguage, loadLocale } = useTranslation();

  // Detect /raportti/<slug> or /report/<slug>. The exact /raportti and
  // /report pages don't match (no trailing segment) so they keep using
  // the query-param branch.
  const slugMatch = location.pathname.match(/^\/(?:raportti|report)\/([^/?#]+)/);
  const slug = slugMatch ? slugMatch[1] : null;
  const [resolvedSlugId, setResolvedSlugId] = useState(null);
  useEffect(() => {
    if (!slug) { setResolvedSlugId(null); return; }
    let cancelled = false;
    fetch(API_ENDPOINTS.INSTANCE_BY_SLUG(slug))
      .then(r => (r.ok ? r.json() : null))
      .then(inst => { if (!cancelled && inst?.id) setResolvedSlugId(inst.id); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  const instanceId = instanceIdProp
    || searchParams.get('kid')
    || searchParams.get('instance')
    || searchParams.get('instanceId')
    || resolvedSlugId
    || null;

  const [edits, setEdits] = useState({});
  const [translations, setTranslations] = useState({});
  const [me, setMe] = useState(undefined);
  const [editMode, setEditModeRaw] = useState(false);
  const [editSessionId, setEditSessionId] = useState(null);
  const [savingKeys, setSavingKeys] = useState(new Set());
  const [instanceLanguage, setInstanceLanguage] = useState('fi');
  const [availableLanguages, setAvailableLanguages] = useState(['fi']);

  const langParam = searchParams.get('lang');
  // Any language the report has a translation in (#1442), not only fi / en / sv
  const viewingLanguage = normalizeReportLanguage(langParam)
    || normalizeReportLanguage(uiLanguage)
    || instanceLanguage;
  const isTranslatedView = viewingLanguage !== instanceLanguage;

  // A new session UUID is generated each time edit-mode flips on. The
  // version-history view groups edits by this id, so an admin can see
  // "this batch of changes happened together".
  const setEditMode = useCallback((next) => {
    setEditModeRaw(prev => {
      const value = typeof next === 'function' ? next(prev) : next;
      // Editing only allowed in the instance language
      if (value && viewingLanguage !== instanceLanguage) return prev;
      if (value && !prev) {
        const newId = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setEditSessionId(newId);
      } else if (!value && prev) {
        setEditSessionId(null);
      }
      return value;
    });
  }, [viewingLanguage, instanceLanguage]);

  useEffect(() => {
    let cancelled = false;
    fetch(withBackendPath('/auth/me'), { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(u => { if (!cancelled) setMe(u || null); })
      .catch(() => { if (!cancelled) setMe(null); });
    return () => { cancelled = true; };
  }, []);

  // Load instance language + available translation languages
  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    (async () => {
      try {
        const [instRes, langsRes] = await Promise.all([
          fetch(API_ENDPOINTS.INSTANCE(instanceId)),
          fetch(API_ENDPOINTS.REPORT_TRANSLATION_LANGUAGES(instanceId)),
        ]);
        if (cancelled) return;
        if (instRes.ok) {
          const inst = await instRes.json();
          const raw = inst?.language ?? inst?.settings?.language ?? 'fi';
          const lang = normalizeInstanceLanguage(raw);
          setInstanceLanguage(lang);
          const covered = langsRes.ok
            ? ((await langsRes.json()).languages || []).map((l) => l.language)
            : [];
          const available = [...new Set([lang, ...covered])];
          setAvailableLanguages(available);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [instanceId]);

  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    fetch(API_ENDPOINTS.REPORT_EDITS(instanceId))
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (!cancelled && data?.edits) setEdits(data.edits); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [instanceId]);

  // Fetch translations when viewing a non-instance language
  useEffect(() => {
    if (!instanceId || !isTranslatedView) {
      setTranslations({});
      return;
    }
    let cancelled = false;
    fetch(API_ENDPOINTS.REPORT_TRANSLATIONS(instanceId, viewingLanguage))
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!cancelled && data?.translations) setTranslations(data.translations);
      })
      .catch(() => { if (!cancelled) setTranslations({}); });
    return () => { cancelled = true; };
  }, [instanceId, viewingLanguage, isTranslatedView]);

  // Keep UI chrome language in sync with viewing language on report routes
  useEffect(() => {
    const onReport = /^\/(?:raportti|report)(\/|$)/.test(location.pathname);
    if (!onReport) return;
    if (!hasStaticUi(viewingLanguage)) {
      loadLocale(viewingLanguage).catch((err) => console.error(`Report interface text for ${viewingLanguage} failed to load:`, err));
    }
    if (uiLanguage !== viewingLanguage) setUiLanguage(viewingLanguage);
  }, [location.pathname, viewingLanguage, uiLanguage, setUiLanguage, loadLocale]);

  // Exit edit mode when switching to a translated view
  useEffect(() => {
    if (isTranslatedView && editMode) setEditModeRaw(false);
  }, [isTranslatedView, editMode]);

  const setViewLanguage = useCallback((lang) => {
    const next = normalizeReportLanguage(lang) || instanceLanguage;
    setUiLanguage(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === instanceLanguage) p.delete('lang');
      else p.set('lang', next);
      return p;
    }, { replace: true });
  }, [setUiLanguage, setSearchParams, instanceLanguage]);

  // Who may edit this report: an admin, or someone with a facilitate grant on
  // this Kuutio (me.grants) — the backend's requireGrant('instance', 'facilitate').
  const canEdit = hasRole(me, 'admin') || canOnInstance(me, instanceId, 'facilitate');

  const getOriginal = useCallback(
    (fieldKey, fallback = '') => (edits[fieldKey]?.text ?? fallback),
    [edits],
  );

  const get = useCallback(
    (fieldKey, fallback = '') => {
      if (isTranslatedView && translations[fieldKey] != null) {
        return translations[fieldKey];
      }
      return edits[fieldKey]?.text ?? fallback;
    },
    [edits, translations, isTranslatedView],
  );

  // For client-side derived strings (highlights, findings, scenario
  // one-liners). Unlike get(), never consults admin edits — so the
  // default instance-language report stays byte-identical.
  const getTranslated = useCallback(
    (fieldKey, fallback = '') =>
      (isTranslatedView ? (translations[fieldKey] ?? fallback) : fallback),
    [isTranslatedView, translations],
  );

  const isEdited = useCallback((fieldKey) => !!edits[fieldKey], [edits]);

  const isTranslated = useCallback(
    (fieldKey) => isTranslatedView && translations[fieldKey] != null,
    [isTranslatedView, translations],
  );

  // Returns a promise so modal can show pending state and close on success.
  const save = useCallback(
    async (fieldKey, text) => {
      if (!instanceId || !canEdit || isTranslatedView) return;

      setSavingKeys(s => { const n = new Set(s); n.add(fieldKey); return n; });
      try {
        const r = await fetch(API_ENDPOINTS.REPORT_EDIT_FIELD(instanceId, fieldKey), {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, editSessionId }),
        });
        if (!r.ok) throw new Error(`PUT ${fieldKey} → ${r.status}`);
        const data = await r.json();
        if (data?.edit) {
          setEdits(e => ({ ...e, [fieldKey]: data.edit }));
        }
      } finally {
        setSavingKeys(s => { const n = new Set(s); n.delete(fieldKey); return n; });
      }
    },
    [instanceId, canEdit, editSessionId, isTranslatedView],
  );

  const value = useMemo(
    () => ({
      canEdit,
      me,
      editMode: editMode && !isTranslatedView,
      setEditMode,
      editSessionId,
      get,
      getTranslated,
      getOriginal,
      isEdited,
      isTranslated,
      save,
      savingKeys,
      edits,
      translations,
      instanceId,
      instanceLanguage,
      viewingLanguage,
      isTranslatedView,
      availableLanguages,
      setViewLanguage,
    }),
    [
      canEdit, me, editMode, isTranslatedView, setEditMode, editSessionId,
      get, getTranslated, getOriginal, isEdited, isTranslated, save, savingKeys, edits,
      translations, instanceId, instanceLanguage, viewingLanguage,
      availableLanguages, setViewLanguage,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
